import convertors from './convertors';
import { getText, getChildren, isElement } from './dom';

interface Schema {
    collections: Record<string, string[]>;
    freeforms: Record<string, string[]>;
    hybrids: string[];
    types: Record<string, Record<string, string>>;
}

interface UnmarshalerNode {
    _value: unknown;
    [key: string]: unknown;
}

const toProperty = (() => {
    const re = /^[A-Z]+/;
    const insert = (m: string): string =>
        m.length === 1
            ? m.toLowerCase()
            : m.substr(0, m.length - 1).toLowerCase() + m.charAt(m.length - 1);
    return (str: string): string => str.replace(re, insert);
})();

const hasPair = (
    map: Record<string, string[]>,
    parentName: string,
    childName: string
): boolean =>
    map[parentName] != null && map[parentName].indexOf(childName) >= 0;

export default class Unmarshaler {
    private _collections: Record<string, string[]>;
    private _freeforms: Record<string, string[]>;
    private _hybrids: string[];
    private _types: Record<string, Record<string, string>>;

    constructor({ collections, freeforms, hybrids, types }: Schema) {
        this._collections = collections;
        this._freeforms = freeforms;
        this._hybrids = hybrids;
        this._types = types;
    }

    unmarshal(xml: Element): UnmarshalerNode {
        return this._createNode(xml);
    }

    private _createNode(xml: Element): UnmarshalerNode {
        const node: UnmarshalerNode = Object.create(null);
        node._value = this._isHybrid(xml.nodeName)
            ? xml
            : this._convertPropertyValue(getText(xml), xml.nodeName, '_value');
        this._copyAttributes(node, xml);
        this._createCollections(node, xml);
        this._createChildren(node, xml);
        return node;
    }

    private _createFreeformNode(xml: Element): UnmarshalerNode {
        const node: UnmarshalerNode = Object.create(null);
        this._copyAttributes(node, xml);
        node._value = xml;
        return node;
    }

    private _copyAttributes(node: UnmarshalerNode, xml: Element): void {
        const parentName = xml.nodeName;
        for (let i = 0; i < xml.attributes.length; ++i) {
            const attrNode = xml.attributes[i];
            const name = attrNode.nodeName;
            const rawValue = attrNode.nodeValue;
            if (rawValue !== null) {
                node[name] = this._convertPropertyValue(
                    rawValue,
                    parentName,
                    name
                );
            }
        }
    }

    private _createCollections(node: UnmarshalerNode, xml: Element): void {
        if (this._collections[xml.nodeName] != null) {
            this._collections[xml.nodeName].forEach((childName: string) => {
                node[toProperty(childName)] = [];
            });
        }
    }

    private _createChildren(node: UnmarshalerNode, xml: Element): void {
        const parentName = xml.nodeName;
        getChildren(xml, isElement).forEach((child: Node) => {
            const childElement = child as Element;
            const childName = childElement.nodeName;
            const prop = toProperty(childName);
            if (this._isFreeformParent(parentName)) {
                if (this._isFreeformChild(parentName, childName)) {
                    this._addFreeformChild(childElement, prop, node);
                }
            } else {
                this._addNodeChild(
                    childElement,
                    prop,
                    node,
                    parentName,
                    childName
                );
            }
        });
    }

    private _addNodeChild(
        child: Element,
        prop: string,
        parentNode: UnmarshalerNode,
        parentName: string,
        childName: string
    ): void {
        const childNode = this._createNode(child);
        if (parentNode[prop] == null) {
            parentNode[prop] = childNode;
        } else if (this._isCollection(parentName, childName)) {
            (parentNode[prop] as UnmarshalerNode[]).push(childNode);
        } else {
            throw new Error(`Multiple values for ${parentName}.${childName}`);
        }
    }

    private _addFreeformChild(
        child: Element,
        prop: string,
        parentNode: UnmarshalerNode
    ): void {
        parentNode[prop] = parentNode[prop] || [];
        (parentNode[prop] as UnmarshalerNode[]).push(
            this._createFreeformNode(child)
        );
    }

    private _convertPropertyValue(
        value: string,
        parentName: string,
        property: string
    ): unknown {
        const typeId =
            this._types[parentName] != null
                ? this._types[parentName][property]
                : null;
        return typeId != null ? convertors[typeId](value) : value;
    }

    private _isCollection(parentName: string, childName: string): boolean {
        return hasPair(this._collections, parentName, childName);
    }

    private _isFreeformParent(name: string): boolean {
        return this._freeforms[name] != null;
    }

    private _isFreeformChild(parentName: string, childName: string): boolean {
        return hasPair(this._freeforms, parentName, childName);
    }

    private _isHybrid(name: string): boolean {
        return this._hybrids.indexOf(name) >= 0;
    }
}
